import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { autoReferralApi } from '../../api/autoReferral.api';
import { companyApi } from '../../api/company.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner, Select } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const MATCH_TYPES = [
  { value: 'company', label: 'شرکت فرستنده' },
  { value: 'user', label: 'کاربر فرستنده' },
  { value: 'title', label: 'جستجو بر اساس عنوان' },
  { value: 'content', label: 'محتوای تیکت (کلیدواژه)' },
  { value: 'time', label: 'بازه زمانی' },
];

const emptyForm = () => ({
  match_type: 'company',
  company_id: '',
  user_id: '',
  content_prompt: '',
  time_start: '08:00',
  time_end: '17:00',
  priority: 0,
  assignee_employee_ids: [],
  enabled: true,
});

function matchTypeLabel(type) {
  return MATCH_TYPES.find((m) => m.value === type)?.label || type;
}

export default function AutoReferralRulesPage() {
  const { addToast } = useUI();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [masterEnabled, setMasterEnabled] = useState(true);
  const [schemaReady, setSchemaReady] = useState(true);
  const [rules, setRules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editGroupId, setEditGroupId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, companiesRes] = await Promise.all([
        autoReferralApi.getSettings(),
        companyApi.getCompanyList(),
      ]);
      const data = settingsRes.data || {};
      setMasterEnabled(data.settings?.enabled !== false);
      setRules(Array.isArray(data.rules) ? data.rules : []);
      setEmployees(Array.isArray(data.employees) ? data.employees : []);
      setSchemaReady(data.schema_ready !== false);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
    } catch {
      addToast('خطا در بارگذاری قوانین ارجاع خودکار', 'error');
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, { paused: !!editGroupId || saving });

  useEffect(() => {
    if (!form.company_id) {
      setCompanyUsers([]);
      return;
    }
    companyApi.getCompanyUsers(form.company_id)
      .then((res) => setCompanyUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCompanyUsers([]));
  }, [form.company_id]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditGroupId(null);
  };

  const startEdit = (rule) => {
    setEditGroupId(rule.rule_group_id);
    setForm({
      match_type: rule.match_type,
      company_id: rule.company_id ? String(rule.company_id) : '',
      user_id: rule.user_id ? String(rule.user_id) : '',
      content_prompt: rule.content_prompt || '',
      time_start: rule.time_start || '08:00',
      time_end: rule.time_end || '17:00',
      priority: rule.priority || 0,
      assignee_employee_ids: (rule.assignees || []).map((a) => a.employee_id),
      enabled: rule.enabled !== false,
    });
  };

  const toggleAssignee = (employeeId) => {
    setForm((prev) => {
      const ids = prev.assignee_employee_ids.includes(employeeId)
        ? prev.assignee_employee_ids.filter((id) => id !== employeeId)
        : [...prev.assignee_employee_ids, employeeId];
      return { ...prev, assignee_employee_ids: ids };
    });
  };

  const saveMasterToggle = async (enabled) => {
    setMasterEnabled(enabled);
    try {
      await autoReferralApi.saveSettings({ enabled });
      addToast(enabled ? 'ارجاع خودکار فعال شد' : 'ارجاع خودکار غیرفعال شد', 'success');
    } catch {
      addToast('خطا در ذخیره تنظیم کلی', 'error');
      setMasterEnabled(!enabled);
    }
  };

  const saveRule = async () => {
    if (form.match_type === 'title' && !form.content_prompt.trim()) {
      addToast('متن فیلتر عنوان را وارد کنید', 'error');
      return;
    }
    if (!form.assignee_employee_ids.length) {
      addToast('حداقل یک گیرنده انتخاب کنید', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rule_group_id: editGroupId || undefined,
        company_id: form.company_id ? Number(form.company_id) : null,
        user_id: form.user_id ? Number(form.user_id) : null,
        priority: Number(form.priority) || 0,
      };
      const res = await autoReferralApi.saveRule(payload);
      setRules(Array.isArray(res.data?.rules) ? res.data.rules : []);
      addToast(editGroupId ? 'قانون ویرایش شد' : 'قانون اضافه شد', 'success');
      resetForm();
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در ذخیره قانون', 'error');
    }
    setSaving(false);
  };

  const toggleRule = async (ruleGroupId, enabled) => {
    try {
      const res = await autoReferralApi.toggleRule({ rule_group_id: ruleGroupId, enabled });
      setRules(Array.isArray(res.data?.rules) ? res.data.rules : []);
      addToast(enabled ? 'قانون فعال شد' : 'قانون غیرفعال شد', 'success');
    } catch {
      addToast('خطا در تغییر وضعیت قانون', 'error');
    }
  };

  const ruleSummary = (rule) => {
    if (rule.match_type === 'company') return `شرکت: ${rule.company_display || rule.company_name || rule.company_id || '—'}`;
    if (rule.match_type === 'user') return `فرستنده: ${rule.sender_username || rule.user_id || '—'}`;
    if (rule.match_type === 'title') {
      return rule.content_prompt
        ? `فیلتر عنوان: «${rule.content_prompt}»`
        : 'فیلتر عنوان خالی';
    }
    if (rule.match_type === 'content') {
      return rule.content_prompt
        ? `کلیدواژه: «${rule.content_prompt}»`
        : 'هر محتوایی (AI بعداً)';
    }
    if (rule.match_type === 'time') return `ساعت ${rule.time_start || '—'} تا ${rule.time_end || '—'}`;
    return '';
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="ارجاع خودکار تیکت"
        subtitle="وقتی تیکتی وارد صف دپارتمان شما می‌شود، بر اساس قوانین زیر به کارمند ارجاع داده می‌شود"
        action={
          <Link to="/settings" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}>
            ← بازگشت به تنظیمات
          </Link>
        }
      />

      {!schemaReady && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 12,
          padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#92400e',
        }}
        >
          جدول قوانین روی سرور ساخته نشده — پس از اجرای SQL migration این صفحه کامل کار می‌کند.
        </div>
      )}

      <div style={{
        background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.12)',
        padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>فعال‌سازی کلی</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            با غیرفعال کردن، هیچ قانونی اجرا نمی‌شود (قوانین حذف نمی‌شوند).
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={masterEnabled}
            onChange={(e) => saveMasterToggle(e.target.checked)}
          />
          ارجاع خودکار فعال
        </label>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : (
        <div className="pj-split-grid pj-split-grid--wide">
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>قوانین تعریف‌شده</h3>
            {rules.length === 0 ? (
              <p style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '24px 0' }}>
                هنوز قانونی تعریف نشده — از فرم کنار صفحه اولین قانون را بسازید.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rules.map((rule) => (
                  <div
                    key={rule.rule_group_id}
                    style={{
                      padding: '12px 14px', borderRadius: 10,
                      border: `1.5px solid ${editGroupId === rule.rule_group_id ? '#29AAE1' : '#e2e8f0'}`,
                      background: editGroupId === rule.rule_group_id ? '#EDF3FC' : rule.enabled ? '#fff' : '#f8fafc',
                      opacity: rule.enabled ? 1 : 0.75,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                          {matchTypeLabel(rule.match_type)}
                          {!rule.enabled && (
                            <span style={{ fontSize: 10, color: '#94a3b8', marginRight: 8 }}>(غیرفعال)</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{ruleSummary(rule)}</div>
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 6 }}>
                          گیرنده‌ها: {(rule.assignees || []).map((a) => a.username).join('، ') || '—'}
                          {' · '}
                          اولویت: {rule.priority}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Btn size="sm" variant="secondary" onClick={() => startEdit(rule)}>ویرایش</Btn>
                        <Btn
                          size="sm"
                          variant="secondary"
                          onClick={() => toggleRule(rule.rule_group_id, !rule.enabled)}
                        >
                          {rule.enabled ? 'غیرفعال' : 'فعال'}
                        </Btn>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>
              {editGroupId ? 'ویرایش قانون' : 'افزودن قانون'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Select
                label="نوع شرط"
                value={form.match_type}
                onChange={(e) => setForm((p) => ({ ...p, match_type: e.target.value }))}
                options={MATCH_TYPES.map((m) => ({ value: m.value, label: m.label }))}
              />

              {form.match_type === 'company' && (
                <Select
                  label="شرکت"
                  value={form.company_id}
                  onChange={(e) => setForm((p) => ({ ...p, company_id: e.target.value }))}
                  options={[
                    { value: '', label: '— انتخاب شرکت —' },
                    ...companies.map((c) => ({ value: String(c.id), label: c.display_name || c.name })),
                  ]}
                />
              )}

              {form.match_type === 'user' && (
                <>
                  <Select
                    label="شرکت (برای فیلتر کاربران)"
                    value={form.company_id}
                    onChange={(e) => setForm((p) => ({ ...p, company_id: e.target.value, user_id: '' }))}
                    options={[
                      { value: '', label: '— انتخاب شرکت —' },
                      ...companies.map((c) => ({ value: String(c.id), label: c.display_name || c.name })),
                    ]}
                  />
                  <Select
                    label="کاربر فرستنده"
                    value={form.user_id}
                    onChange={(e) => setForm((p) => ({ ...p, user_id: e.target.value }))}
                    options={[
                      { value: '', label: '— انتخاب کاربر —' },
                      ...companyUsers.map((u) => ({
                        value: String(u.user_id || u.id),
                        label: u.username || u.name,
                      })),
                    ]}
                  />
                </>
              )}

              {form.match_type === 'title' && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                    فیلتر عنوان *
                  </span>
                  <textarea
                    value={form.content_prompt}
                    onChange={(e) => setForm((p) => ({ ...p, content_prompt: e.target.value }))}
                    placeholder="عبارت مرجع برای مقایسه با عنوان تیکت (امبدینگ Tooka-SBERT)"
                    rows={3}
                    style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </label>
              )}

              {form.match_type === 'content' && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                    کلیدواژه / یادداشت AI (اختیاری)
                  </span>
                  <textarea
                    value={form.content_prompt}
                    onChange={(e) => setForm((p) => ({ ...p, content_prompt: e.target.value }))}
                    placeholder="فعلاً: جستجوی ساده در عنوان و متن. خالی = همه تیکت‌ها."
                    rows={3}
                    style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </label>
              )}

              {form.match_type === 'time' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>از ساعت</span>
                    <input
                      type="time"
                      value={form.time_start}
                      onChange={(e) => setForm((p) => ({ ...p, time_start: e.target.value }))}
                      style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>تا ساعت</span>
                    <input
                      type="time"
                      value={form.time_end}
                      onChange={(e) => setForm((p) => ({ ...p, time_end: e.target.value }))}
                      style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}
                    />
                  </label>
                </div>
              )}

              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>اولویت (بیشتر = زودتر)</span>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
                  style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}
                />
              </label>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 8 }}>گیرنده‌ها *</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                  {employees.map((emp) => (
                    <label key={emp.employee_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.assignee_employee_ids.includes(emp.employee_id)}
                        onChange={() => toggleAssignee(emp.employee_id)}
                      />
                      {emp.label || emp.username}
                    </label>
                  ))}
                </div>
                {form.assignee_employee_ids.length > 1 && (
                  <p style={{ fontSize: 11, color: '#64748b', marginTop: 6, lineHeight: 1.6 }}>
                    در صورت چند گیرنده، تیکت به اولین فرد انتخاب‌شده (بر اساس ترتیب لیست) ارجاع می‌شود.
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Btn onClick={saveRule} loading={saving}>{editGroupId ? 'ذخیره' : 'افزودن'}</Btn>
                {editGroupId && <Btn variant="secondary" onClick={resetForm}>انصراف</Btn>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
